import { defineStore } from 'pinia';

import {
  fetchChatInfo as fetchChatInfoAPI,
  fetchChatList as fetchChatListAPI,
  fetchChatLogs as fetchChatLogsAPI,
} from '@/api/apiService';
import { debugLog } from '@/static/develop.js';
import { ChatItem } from '@/types/components';
import { IMessageMt } from '@/types/socket';

export const QRCodeType = Object.freeze({
  CONSULTATION_CALL: 'CONSULTATION_CALL',
  GENERAL_CALL_PARTNER: 'GENERAL_CALL_PARTNER',
  GENERAL_CALL_GUEST: 'GENERAL_CALL_GUEST',
  MEETING_CALL_PARTNER: 'MEETING_CALL_PARTNER',
  MEETING_CALL_GUEST: 'MEETING_CALL_GUEST',
  CONFERENCE_CALL_GUEST: 'CONFERENCE_CALL_GUEST',
  CONFERENCE_CALL_PARTNER: 'CONFERENCE_CALL_PARTNER',
  isGuest: function (type) {
    return [this.GENERAL_CALL_GUEST, this.MEETING_CALL_GUEST, this.CONFERENCE_CALL_GUEST].includes(type);
  },
  isEmployee: function (type) {
    return [this.GENERAL_CALL_PARTNER, this.MEETING_CALL_PARTNER, this.CONFERENCE_CALL_PARTNER].includes(type);
  },
  isConsultationType: function (type) {
    return type === this.CONSULTATION_CALL;
  },
});

export const useChatStore = defineStore('chat', {
  persist: true,
  state: () => ({
    chatList: [],
    errorChats: {} as Record<string, IMessageMt[]>,
    chatInfo: {} as ChatItem,
    chatLogs: [],
    nextOffset: null,
    hasMore: true,
    isLoading: false,
    error: null,
  }),

  actions: {
    async fetchChatList(onlyMine = false) {
      this.isLoading = true;
      try {
        if (this.hasMore) {
          const response = await fetchChatListAPI(this.nextOffset, onlyMine);
          this.chatList = [...this.chatList, ...response.data.datas];
          debugLog(`this.chatList: ${JSON.stringify(this.chatList)}`);
          this.nextOffset = response.data.nextOffset;
          this.hasMore = response.data.hasMore;
        }
        this.isLoading = false;
      } catch (err) {
        this.error = err;
        this.isLoading = false;
      }
    },

    getErrorChatList(chatId: string): IMessageMt[] {
      return this.errorChats[chatId] || [];
    },
    setErrorChat(chatId: string, message: IMessageMt) {
      if (!this.errorChats[chatId]) {
        this.errorChats[chatId] = [];
      }

      const errorChat = this.errorChats[chatId].find(({ socketId }) => socketId === message.socketId);
      if (!errorChat) {
        this.errorChats[chatId].push(message);
      }
    },
    deleteErrorChat(chatId: string, id: string) {
      if (this.errorChats[chatId]) {
        const inx = this.errorChats[chatId].findIndex(({ socketId }) => socketId === id);
        this.errorChats[chatId].splice(inx, inx === -1 ? 0 : 1);

        if (this.errorChats[chatId].length === 0) {
          delete this.errorChats[chatId];
        }
      }
    },

    async fetchChatInfo(chatId) {
      this.isLoading = true;
      this.chatInfo = null; // 새 채팅방을 로드하기 전에 상태 초기화
      try {
        const response = await fetchChatInfoAPI(chatId);
        this.chatInfo = response.data;
        this.isLoading = false;
      } catch (err) {
        this.error = err;
        this.isLoading = false;
      }
    },

    async fetchChatLogs(chatId, offset = null) {
      this.isLoading = true;
      if (!offset) {
        this.chatLogs = [];
      }
      try {
        const response = await fetchChatLogsAPI(chatId, offset);
        if (offset) {
          this.chatLogs = [...this.chatLogs, ...response.data.datas];
        } else {
          this.chatLogs = response.data.datas;
        }
        this.nextOffset = response.data.nextOffset;
        this.isLoading = false;
      } catch (err) {
        this.error = err;
        this.isLoading = false;
      }
    },

    setChatInfo(chat: ChatItem) {
      this.chatInfo = chat;
    },
    getChatInfo() {
      return this.chatInfo;
    },
    resetChatInfo() {
      this.chatInfo = {} as ChatItem;
    },
  },
});
